/**
 * After mapping, the uploaded CSV must use headers expected by the API:
 * account_id, campaign_id, spend, impressions
 * (source is sent separately as form data on upload.)
 */

export type InvoiceValidationTargetKey =
  | 'account_id'
  | 'campaign_id'
  | 'spend'
  | 'impressions';

export const INVOICE_VALIDATION_TEMPLATE_COLUMNS: {
  key: InvoiceValidationTargetKey;
  /** Exact header written to the CSV sent to the server */
  templateHeader: string;
  label: string;
  required: boolean;
}[] = [
  { key: 'account_id', templateHeader: 'account_id', label: 'Account id', required: true },
  { key: 'campaign_id', templateHeader: 'campaign_id', label: 'Campaign id', required: true },
  { key: 'spend', templateHeader: 'spend', label: 'Spend', required: true },
  { key: 'impressions', templateHeader: 'impressions', label: 'Impressions / qty', required: false },
];

export const REQUIRED_MAPPED_INVOICE_VALIDATION_KEYS: InvoiceValidationTargetKey[] = [
  'account_id',
  'campaign_id',
  'spend',
];

export function stripBom(text: string): string {
  if (text.charCodeAt(0) === 0xfeff) {
    return text.slice(1);
  }
  return text;
}

/** RFC-style CSV parse: commas, double-quote escapes, newlines in quoted fields. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let i = 0;
  let inQuotes = false;
  const len = text.length;

  while (i < len) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += c;
      i += 1;
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (c === ',') {
      row.push(field);
      field = '';
      i += 1;
      continue;
    }
    if (c === '\r') {
      i += 1;
      continue;
    }
    if (c === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      i += 1;
      continue;
    }
    field += c;
    i += 1;
  }
  row.push(field);
  if (row.some((cell) => cell.length > 0)) {
    rows.push(row);
  }
  return rows;
}

export function escapeCsvField(s: string): string {
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function guessColumnMapping(
  headers: string[],
): Partial<Record<InvoiceValidationTargetKey, number>> {
  const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ').replace(/_/g, ' ');
  const headerNorm = headers.map(norm);

  const aliases: Record<InvoiceValidationTargetKey, string[]> = {
    account_id: ['account id', 'accountid', 'acct id', 'acctid'],
    campaign_id: ['campaign id', 'campaignid', 'cmp id', 'cmpid'],
    spend: ['amount', 'spend', 'cost', 'total', 'advertiser spend', 'net spend', 'gross spend'],
    impressions: ['impressions', 'imps', 'qty', 'quantity', 'volume', 'delivered imps'],
  };

  const result: Partial<Record<InvoiceValidationTargetKey, number>> = {};

  for (const col of INVOICE_VALIDATION_TEMPLATE_COLUMNS) {
    const labelNorm = norm(col.label);
    for (let i = 0; i < headerNorm.length; i += 1) {
      if (headerNorm[i] === labelNorm) {
        result[col.key] = i;
        break;
      }
    }
  }

  for (const col of INVOICE_VALIDATION_TEMPLATE_COLUMNS) {
    if (result[col.key] !== undefined) {
      continue;
    }
    const want = norm(col.templateHeader);
    for (let i = 0; i < headerNorm.length; i += 1) {
      if (headerNorm[i] === want) {
        result[col.key] = i;
        break;
      }
    }
  }

  for (const key of Object.keys(aliases) as InvoiceValidationTargetKey[]) {
    if (result[key] !== undefined) {
      continue;
    }
    const patterns = aliases[key];
    for (let i = 0; i < headerNorm.length; i += 1) {
      const h = headerNorm[i];
      if (patterns.some((p) => h === p)) {
        result[key] = i;
        break;
      }
    }
  }

  return result;
}

export type ColumnMappingState = Partial<Record<InvoiceValidationTargetKey, number | null>>;

/** True when the user picked a concrete column index (0-based), including column 0. */
export function isMappedColumnIndex(idx: number | null | undefined): idx is number {
  return typeof idx === 'number' && Number.isFinite(idx) && idx >= 0;
}

export function buildMappedInvoiceValidationCsv(
  rows: string[][],
  mapping: ColumnMappingState,
): string {
  const headerRow = INVOICE_VALIDATION_TEMPLATE_COLUMNS.map((c) => c.templateHeader);
  const out: string[][] = [headerRow];
  const dataRows = rows.slice(1);

  for (const dataRow of dataRows) {
    if (dataRow.every((cell) => !String(cell).trim())) {
      continue;
    }
    const outRow: string[] = [];
    for (const col of INVOICE_VALIDATION_TEMPLATE_COLUMNS) {
      const idx = mapping[col.key];
      if (isMappedColumnIndex(idx) && idx < dataRow.length) {
        outRow.push(dataRow[idx] ?? '');
      } else {
        outRow.push('');
      }
    }
    out.push(outRow);
  }

  return out.map((r) => r.map(escapeCsvField).join(',')).join('\r\n');
}

/**
 * Required: account_id, campaign_id, spend mapped; platform (source) chosen for the upload
 * (passed separately in the multipart request).
 */
export function isInvoiceValidationMappingComplete(
  mapping: ColumnMappingState,
  defaultSource: string,
): boolean {
  for (const key of REQUIRED_MAPPED_INVOICE_VALIDATION_KEYS) {
    if (!isMappedColumnIndex(mapping[key])) {
      return false;
    }
  }
  return defaultSource.trim().length > 0;
}

export async function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}
